import React, { useState, useEffect } from 'react';

// Import the JSON-based configuration
import appConfig, { ENDPOINT_KEYS } from './config/appConfig';


const EditIntegrationFieldDialog = ({
  isOpen,
  onClose,
  selectedRequirements,
  field,
  corporateAccount,
  currentProject,
  login_token,
  systemType,
  systemId,
  sourceOrProviderSystemId,
  sourceOrProviderSystemName
}) => {
  const [fieldDescription, setFieldDescription] = useState('');
  const [fieldDataType, setFieldDataType] = useState('');
  const [fieldSize, setFieldSize] = useState('');
  const [fieldOptionalOrMandatory, setFieldOptionalOrMandatory] = useState('');
  const [fieldTransformation, setFieldTransformation] = useState('');
  const [fieldDataValidation, setFieldDataValidation] = useState('');
  const [fieldDataSecurity, setFieldDataSecurity] = useState('');
  const [status, setStatus] = useState('');
  const [mapsToProviderField, setMapsToProviderField] = useState('');
  const [statuses, setStatuses] = useState([]);
  const [providerFields, setProviderFields] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingProviderFields, setIsLoadingProviderFields] = useState(false);
  const [error, setError] = useState('');

  // Updated Dialog styles with fixed layout
  const DialogStyles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
      padding: '20px'
    },
    dialog: {
      backgroundColor: 'white',
      borderRadius: '8px',
      width: '700px',
      maxWidth: '90vw',
      height: '90vh',
      maxHeight: '90vh',
      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    },
    header: {
      backgroundColor: '#CCCCFF',
      textAlign: 'center',
      padding: '10px 20px',
      borderRadius: '8px 8px 0 0',
      fontSize: '18px',
      fontWeight: 'bold',
      flexShrink: 0
    },
    content: {
      flex: 1,
      overflowY: 'auto',
      padding: '20px',
      maxHeight: 'calc(90vh - 120px)'
    },
    footer: {
      padding: '15px 20px',
      borderTop: '1px solid #e5e7eb',
      backgroundColor: '#f9fafb',
      borderRadius: '0 0 8px 8px',
      flexShrink: 0
    }
  };

  const getIntegrationId = () => {
    if (selectedRequirements && selectedRequirements.size === 1) {
      return Array.from(selectedRequirements)[0];
    }
    return null;
  };

  // Initialize form with field data when dialog opens or field changes
  useEffect(() => {
    if (isOpen && field) {
      setFieldDescription(field.field_description || '');
      setFieldDataType(field.field_data_type || '');
      setFieldSize(field.field_size || '');
      setFieldOptionalOrMandatory(field.field_optional_or_mandatory || 'Optional');
      setFieldTransformation(field.field_transformation || '');
      setFieldDataValidation(field.field_data_validation || '');
      setFieldDataSecurity(field.field_data_security || '');
      setStatus(field.status || '');
      setMapsToProviderField(field.maps_to_provider_system_field_name || '');
      setError(''); // Reset error when dialog opens
      fetchStatuses();
      // Only fetch provider fields if systemType is CONSUMER
      if (systemType === 'CONSUMER') {
        fetchProviderFields();
      }
    }
  }, [isOpen, field, systemType]);

  const fetchStatuses = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(appConfig.getUrl('/get_statuses'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          project_id: currentProject,
          entity: 'REQUIREMENT',
          search_query: ''
        })
      });

      const data = await response.json();
      if (data.status === 'Success') {
        const statusOptions = data.entity_statuses.map(s => s.status);
        setStatuses(statusOptions);
      } else {
        throw new Error(data.status_description || 'Failed to fetch statuses');
      }
    } catch (err) {
      console.error('Error fetching statuses:', err);
      setError('Failed to load status options');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchProviderFields = async () => {

  console.log('Fetching provider fields for system:', sourceOrProviderSystemId);

    if (!sourceOrProviderSystemId) {
      console.log('No sourceOrProviderSystemId provided');
      return;
    }

    setIsLoadingProviderFields(true);
    try {
      console.log('Fetching provider fields with params:', {
        corporate_account: corporateAccount,
        project_id: currentProject,
        system_id: sourceOrProviderSystemId,
        system_type: "PROVIDER",
        integration_id: getIntegrationId(),
        filter_by_status: [],
        search_query: "",
        sort_criteria: "field_name"
      });

      const response = await fetch(appConfig.getUrl('/get_integration_requirement_field_list'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          project_id: currentProject,
          system_id: sourceOrProviderSystemId,
          system_type: "PROVIDER",
          integration_id: getIntegrationId(),
          filter_by_status: [],
          search_query: "",
          sort_criteria: "field_name"
        })
      });

      console.log('Provider fields response status:', response.status);
      const data = await response.json();
      console.log('Provider fields response data:', data);

      if (data.status === 'Success' && data.integration_fields_list) {
        setProviderFields(data.integration_fields_list || []);
        console.log('Set provider fields:', data.integration_fields_list.length, 'fields');
      } else {
        setProviderFields([]);
        console.error('Failed to fetch provider fields:', data.status_description);
      }
    } catch (err) {
      console.error('Error fetching provider fields:', err);
      setProviderFields([]);
    } finally {
      setIsLoadingProviderFields(false);
    }
  };

  const handleSubmit = async () => {
    if (!field || !field.field_name) {
      setError('Invalid field data');
      return;
    }

    setIsLoading(true);
    setError('');

    const integrationId = Array.from(selectedRequirements)[0];

    try {
      const response = await fetch(appConfig.getUrl('/update_integration_requirement_field'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          project_id: currentProject,
          system_id: systemId,
          system_type: systemType,
          integration_id: integrationId,
          field_name: field.field_name,
          field_description: fieldDescription,
          field_data_type: fieldDataType,
          field_size: fieldSize,
          field_optional_or_mandatory: fieldOptionalOrMandatory,
          field_transformation: fieldTransformation,
          field_data_validation: fieldDataValidation,
          field_data_security: fieldDataSecurity,
          maps_to_provider_system_field_name: mapsToProviderField || null,
          status: status
        })
      });

      const data = await response.json();
      if (data.status === 'Success') {
        // First show the success message (same pattern as other dialogs)
        setError('Field updated successfully');

        // After a delay, close the dialog
        setTimeout(() => {
          onClose(true);
        }, 2000);
      } else {
        throw new Error(data.status_description || 'Failed to update field');
      }
    } catch (err) {
      setError(err.message);
      console.error('Error updating field:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    onClose(false);
  };

  if (!isOpen || !field) return null;

  return (
    <div style={DialogStyles.overlay}>
      <div style={DialogStyles.dialog}>
        {/* Fixed Header */}
        <div style={DialogStyles.header}>
          Edit Integration Field ({field.field_name})
        </div>

        {/* Scrollable Content */}
        <div style={DialogStyles.content}>
          {/* ===== FIELD IDENTIFICATION SECTION ===== */}
          <div style={{
            backgroundColor: '#f0f9ff',
            border: '1px solid #e0f2fe',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '20px'
          }}>
            <h3 style={{
              fontSize: '16px',
              fontWeight: 'bold',
              marginBottom: '12px',
              color: '#0284c7',
              borderBottom: '2px solid #0284c7',
              paddingBottom: '4px'
            }}>
              🆔 Field Identification
            </h3>
            <div style={{
              padding: '12px',
              backgroundColor: '#dbeafe',
              borderRadius: '6px',
              border: '1px solid #93c5fd'
            }}>
              <label style={{
                display: 'block',
                marginBottom: '5px',
                fontWeight: 'bold',
                color: '#1e40af'
              }}>
                Field Name:
              </label>
              <div style={{
                padding: '8px',
                backgroundColor: '#f8fafc',
                border: '1px solid #cbd5e1',
                borderRadius: '4px',
                fontSize: '14px',
                color: '#1e293b',
                fontWeight: 'bold'
              }}>
                {field.field_name}
              </div>
            </div>
          </div>

          {/* ===== BASIC INFORMATION SECTION ===== */}
          <div style={{
            backgroundColor: '#f0fdf4',
            border: '1px solid #dcfce7',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '20px'
          }}>
            <h3 style={{
              fontSize: '16px',
              fontWeight: 'bold',
              marginBottom: '16px',
              color: '#059669',
              borderBottom: '2px solid #059669',
              paddingBottom: '4px'
            }}>
              📋 Basic Information
            </h3>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Field Description:
              </label>
              <textarea
                value={fieldDescription}
                onChange={(e) => setFieldDescription(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  resize: 'vertical'
                }}
                rows={3}
                placeholder="Describe the purpose of this field"
              />
            </div>
          </div>

          {/* ===== DATA DEFINITION SECTION ===== */}
          <div style={{
            backgroundColor: '#faf5ff',
            border: '1px solid #f3e8ff',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '20px'
          }}>
            <h3 style={{
              fontSize: '16px',
              fontWeight: 'bold',
              marginBottom: '16px',
              color: '#9333ea',
              borderBottom: '2px solid #9333ea',
              paddingBottom: '4px'
            }}>
              🗃️ Data Definition
            </h3>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px', marginBottom: '15px' }}>
              {/* Data Type */}
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Data Type:
                </label>
                <select
                  value={fieldDataType}
                  onChange={(e) => setFieldDataType(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                >
                  <option value="">Select data type</option>
                  <option value="String">String</option>
                  <option value="Integer">Integer</option>
                  <option value="Float">Float</option>
                  <option value="Boolean">Boolean</option>
                  <option value="Date">Date</option>
                  <option value="DateTime">DateTime</option>
                  <option value="Binary">Binary</option>
                  <option value="JSON">JSON</option>
                  <option value="XML">XML</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {/* Field Size */}
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Field Size:
                </label>
                <input
                  type="text"
                  value={fieldSize}
                  onChange={(e) => setFieldSize(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                  placeholder="e.g. 255, 10.2, MAX"
                />
              </div>

              {/* Optional or Mandatory */}
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Optional or Mandatory:
                </label>
                <select
                  value={fieldOptionalOrMandatory}
                  onChange={(e) => setFieldOptionalOrMandatory(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                >
                  <option value="Optional">Optional</option>
                  <option value="Mandatory">Mandatory</option>
                </select>
              </div>
            </div>
          </div>

          {/* ===== PROCESSING RULES SECTION ===== */}
          <div style={{
            backgroundColor: '#fffbeb',
            border: '1px solid #fef3c7',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '20px'
          }}>
            <h3 style={{
              fontSize: '16px',
              fontWeight: 'bold',
              marginBottom: '16px',
              color: '#d97706',
              borderBottom: '2px solid #d97706',
              paddingBottom: '4px'
            }}>
              ⚙️ Processing Rules
            </h3>

            {/* Transformation Rules */}
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Transformation Rules:
              </label>
              <textarea
                value={fieldTransformation}
                onChange={(e) => setFieldTransformation(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  resize: 'vertical'
                }}
                rows={2}
                placeholder="Describe any data transformations"
              />
            </div>

            {/* Data Validation */}
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Data Validation:
              </label>
              <textarea
                value={fieldDataValidation}
                onChange={(e) => setFieldDataValidation(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  resize: 'vertical'
                }}
                rows={2}
                placeholder="Describe validation rules"
              />
            </div>
          </div>

          {/* ===== FIELD MAPPING SECTION ===== */}
          {/* Only show this section when systemType is CONSUMER */}
          {systemType === 'CONSUMER' && (
            <div style={{
              backgroundColor: '#f0f8f0',
              border: '1px solid #d4edda',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '20px'
            }}>
              <h3 style={{
                fontSize: '16px',
                fontWeight: 'bold',
                marginBottom: '16px',
                color: '#155724',
                borderBottom: '2px solid #155724',
                paddingBottom: '4px'
              }}>
                🔗 Field Mapping
              </h3>

              {/* Maps to Provider Field */}
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Maps to Provider Field:
                </label>
                {isLoadingProviderFields ? (
                  <div style={{
                    width: '100%',
                    padding: '8px',
                    backgroundColor: '#f3f4f6',
                    borderRadius: '4px',
                    color: '#6b7280',
                    fontSize: '14px'
                  }}>
                    Loading provider fields...
                  </div>
                ) : (
                  <select
                    value={mapsToProviderField}
                    onChange={(e) => setMapsToProviderField(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                    disabled={isLoading}
                  >
                    <option value="">Select Provider Field</option>
                    {providerFields.map(field => (
                      <option key={field.field_name} value={field.field_name}>
                        {field.field_name} {field.field_data_type ? `(${field.field_data_type})` : ''}
                      </option>
                    ))}
                  </select>
                )}
                {!sourceOrProviderSystemId && (
                  <div style={{
                    marginTop: '5px',
                    fontSize: '12px',
                    color: '#6b7280',
                    fontStyle: 'italic'
                  }}>
                    Provider system not specified - field mapping unavailable
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ===== SECURITY & STATUS SECTION ===== */}
          <div style={{
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '20px'
          }}>
            <h3 style={{
              fontSize: '16px',
              fontWeight: 'bold',
              marginBottom: '16px',
              color: '#dc2626',
              borderBottom: '2px solid #dc2626',
              paddingBottom: '4px'
            }}>
              🔒 Security & Status
            </h3>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
              {/* Data Security */}
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Data Security:
                </label>
                <textarea
                  value={fieldDataSecurity}
                  onChange={(e) => setFieldDataSecurity(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    resize: 'vertical'
                  }}
                  rows={2}
                  placeholder="Describe security requirements"
                />
              </div>

              {/* Status */}
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Status:
                </label>
                {isLoading ? (
                  <div style={{
                    width: '100%',
                    padding: '8px',
                    backgroundColor: '#f3f4f6',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    color: '#6b7280'
                  }}>
                    Loading statuses...
                  </div>
                ) : (
                  <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  >
                    <option value="">Select status</option>
                    {statuses.map(statusOption => (
                      <option key={statusOption} value={statusOption}>
                        {statusOption}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>
          </div>

        </div>

        {/* Fixed Footer */}
        <div style={DialogStyles.footer}>
          {/* Error message */}
          {error && (
            <div style={{
              marginBottom: '15px',
              padding: '8px',
              borderRadius: '4px',
              backgroundColor: error.includes('successfully') ? '#f0f9ff' : '#fef2f2',
              color: error.includes('successfully') ? '#059669' : '#dc2626',
              border: error.includes('successfully') ? '1px solid #10b981' : '1px solid #ef4444'
            }}>
              {error}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'center', gap: '16px' }}>
            <button
              onClick={handleCancel}
              style={{
                backgroundColor: '#FFFDD0',
                padding: '8px 16px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
              disabled={isLoading}
            >
              Close
            </button>
            <button
              onClick={handleSubmit}
              style={{
                padding: '8px 16px',
                backgroundColor: '#C8F4DE',
                color: 'black',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                opacity: isLoading ? 0.5 : 1,
                display: 'flex',
                alignItems: 'center'
              }}
              disabled={isLoading}
            >
              {isLoading && (
                <svg style={{
                  animation: 'spin 1s linear infinite',
                  marginLeft: '-4px',
                  marginRight: '8px',
                  width: '16px',
                  height: '16px'
                }} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
              {isLoading ? 'Updating...' : 'Update Field'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditIntegrationFieldDialog;